import type { SubmodelElementMetadata } from '../../Metadata/SubmodelElementMetadata';
import type { SubmodelMetadata } from '../../Metadata/SubmodelMetadata';
import type { Submodel } from '../../Metamodel/Submodel';
import type { SubmodelElement } from '../../Metamodel/SubmodelElements/SubmodelElement';
import type { Reference } from '../../Metamodel/SupportTypes/Reference';
import type { OperationRequestValue } from '../../ValueOnly/OperationRequestValue';
import type { OperationResultValue } from '../../ValueOnly/OperationResultValue';
import type { SubmodelElementValue } from '../../ValueOnly/SubmodelElementValue';
import type { ValueOnly } from '../../ValueOnly/ValueOnly';
import type { BaseOperationResult } from '../Interfaces/BaseOperationResult';
import type { OperationRequest } from '../Interfaces/OperationRequest';
import type { OperationResult } from '../Interfaces/OperationResult';
import type { PagedResult } from '../Interfaces/PagedResult';
import type { PagingParams } from '../Interfaces/PagingParams';
import type { SerializationModifiers } from '../Interfaces/SerializationModifiers';
import { assertValidAasResponse, assignPagingParams, assignSearchParams, assignSemanticId, assignSerializationModifiers, base64Url } from '../../Utils/Restutils';
import { ClientBase } from './ClientBase';

export class SubmodelRepoClient extends ClientBase {
  public async getSubmodels(
    semanticId?: Reference,
    idShort?: string,
    pagingParams?: PagingParams,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<PagedResult<Submodel>> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      idShort,
    });
    assignSemanticId(params, semanticId);
    assignPagingParams(params, pagingParams);
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      '/submodels',
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async createSubmodel(
    submodel: Submodel,
    signal?: AbortSignal,
  ): Promise<Submodel> {
    const [request, response] = await this.request(
      'post',
      '/submodels',
      undefined,
      JSON.stringify(submodel),
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelsMetadata(
    semanticId?: Reference,
    idShort?: string,
    pagingParams?: PagingParams,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<PagedResult<SubmodelMetadata>> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      idShort,
    });
    assignSemanticId(params, semanticId);
    assignPagingParams(params, pagingParams);
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      '/submodels/$metadata',
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelsValue(
    semanticId?: Reference,
    idShort?: string,
    pagingParams?: PagingParams,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<PagedResult<ValueOnly>> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      idShort,
    });
    assignSemanticId(params, semanticId);
    assignPagingParams(params, pagingParams);
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      '/submodels/$value',
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelReferences(
    semanticId?: Reference,
    idShort?: string,
    pagingParams?: PagingParams,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<PagedResult<Reference>> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      idShort,
    });
    assignSemanticId(params, semanticId);
    assignPagingParams(params, pagingParams);
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      '/submodels/$reference',
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelsPaths(
    semanticId?: Reference,
    idShort?: string,
    pagingParams?: PagingParams,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<PagedResult<string[]>> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      idShort,
    });
    assignSemanticId(params, semanticId);
    assignPagingParams(params, pagingParams);
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      '/submodels/$path',
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodel(
    id: string,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<Submodel> {
    const params = new URLSearchParams();
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(id)}`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async replaceSubmodel(
    id: string,
    submodel: Submodel,
    signal?: AbortSignal,
  ): Promise<void> {
    const [request, response] = await this.request(
      'put',
      `/submodels/${base64Url(id)}`,
      undefined,
      JSON.stringify(submodel),
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async updateSubmodel(
    id: string,
    submodel: Submodel,
    level?: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level,
    });
    const [request, response] = await this.request(
      'patch',
      `/submodels/${base64Url(id)}`,
      params,
      JSON.stringify(submodel),
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async deleteSubmodel(
    id: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const [request, response] = await this.request(
      'delete',
      `/submodels/${base64Url(id)}`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async getSubmodelMetadata(
    id: string,
    level?: string,
    signal?: AbortSignal,
  ): Promise<SubmodelMetadata> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level,
    });
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(id)}/$metadata`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async updateSubmodelMetadata(
    id: string,
    metadata: SubmodelMetadata,
    level?: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level,
    });
    const [request, response] = await this.request(
      'patch',
      `/submodels/${base64Url(id)}`,
      params,
      JSON.stringify(metadata),
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async getSubmodelValue(
    id: string,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<ValueOnly> {
    const params = new URLSearchParams();
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(id)}/$value`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelReference(
    id: string,
    signal?: AbortSignal,
  ): Promise<Reference> {
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(id)}/$reference`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelPaths(
    id: string,
    level?: string,
    signal?: AbortSignal,
  ): Promise<string[]> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level,
    });
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(id)}/$path`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelElements(
    submodelId: string,
    pagingParams?: PagingParams,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<PagedResult<SubmodelElement>> {
    const params = new URLSearchParams();
    assignPagingParams(params, pagingParams);
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async createSubmodelElement(
    submodelId: string,
    element: SubmodelElement,
    signal?: AbortSignal,
  ): Promise<SubmodelElement> {
    const [request, response] = await this.request(
      'post',
      `/submodels/${base64Url(submodelId)}/submodel-elements`,
      undefined,
      JSON.stringify(element),
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelElementsMetadata(
    submodelId: string,
    pagingParams?: PagingParams,
    level?: string,
    signal?: AbortSignal,
  ): Promise<PagedResult<SubmodelElementMetadata>> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level,
    });
    assignPagingParams(params, pagingParams);
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/$metadata`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelElementsValue(
    submodelId: string,
    pagingParams?: PagingParams,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<PagedResult<SubmodelElementValue>> {
    const params = new URLSearchParams();
    assignPagingParams(params, pagingParams);
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/$value`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelElementReferences(
    submodelId: string,
    pagingParams?: PagingParams,
    signal?: AbortSignal,
  ): Promise<PagedResult<Reference>> {
    const params = new URLSearchParams();
    assignPagingParams(params, pagingParams);
    assignSearchParams(params, {
      level: 'core', // only core is allowed for Content=Reference
    });
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/$reference`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelElementsPaths(
    submodelId: string,
    pagingParams?: PagingParams,
    level?: string,
    signal?: AbortSignal,
  ): Promise<PagedResult<string[]>> {
    const params = new URLSearchParams();
    assignPagingParams(params, pagingParams);
    assignSearchParams(params, {
      level,
    });
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/$path`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelElement(
    submodelId: string,
    idShortPath: string,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<SubmodelElement> {
    const params = new URLSearchParams();
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async createSubmodelElementAtPath(
    submodelId: string,
    idShortPath: string,
    element: SubmodelElement,
    signal?: AbortSignal,
  ): Promise<SubmodelElement> {
    const [request, response] = await this.request(
      'post',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}`,
      undefined,
      JSON.stringify(element),
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async replaceSubmodelElement(
    submodelId: string,
    idShortPath: string,
    element: SubmodelElement,
    level?: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level,
    });
    const [request, response] = await this.request(
      'put',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}`,
      params,
      JSON.stringify(element),
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async updateSubmodelElement(
    submodelId: string,
    idShortPath: string,
    element: SubmodelElement,
    level?: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level,
    });
    const [request, response] = await this.request(
      'patch',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}`,
      params,
      JSON.stringify(element),
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async deleteSubmodelElement(
    submodelId: string,
    idShortPath: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const [request, response] = await this.request(
      'delete',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async getSubmodelElementMetadata(
    submodelId: string,
    idShortPath: string,
    level?: string,
    signal?: AbortSignal,
  ): Promise<SubmodelElementMetadata> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level,
    });
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/$metadata`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async updateSubmodelElementMetadata(
    submodelId: string,
    idShortPath: string,
    metadata: SubmodelElementMetadata,
    level?: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level,
    });
    const [request, response] = await this.request(
      'patch',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/$metadata`,
      params,
      JSON.stringify(metadata),
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async getSubmodelElementValue(
    submodelId: string,
    idShortPath: string,
    serializationModifiers?: SerializationModifiers,
    signal?: AbortSignal,
  ): Promise<SubmodelElementValue> {
    const params = new URLSearchParams();
    assignSerializationModifiers(params, serializationModifiers);
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/$value`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelElementReference(
    submodelId: string,
    idShortPath: string,
    signal?: AbortSignal,
  ): Promise<Reference> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level: 'core', // only core is allowed for Content=Reference
    });
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/$reference`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelElementPaths(
    submodelId: string,
    idShortPath: string,
    level?: string,
    signal?: AbortSignal,
  ): Promise<string[]> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      level,
    });
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/$path`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getSubmodelElementFile(
    submodelId: string,
    idShortPath: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    this.headers.set('Accept', 'application/octet-stream');
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/attachment`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response, 'blob');
  }

  public async setSubmodelElementFile(
    submodelId: string,
    idShortPath: string,
    file: Blob,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', fileName);
    this.headers.set('Content-Type', 'multipart/form-data');
    const [request, response] = await this.request(
      'put',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/attachment`,
      undefined,
      formData,
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async deleteSubmodelElementFile(
    submodelId: string,
    idShortPath: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const [request, response] = await this.request(
      'delete',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/attachment`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async invokeOperation(
    submodelId: string,
    idShortPath: string,
    operationRequest: OperationRequest,
    invokeAsync?: boolean,
    signal?: AbortSignal,
  ): Promise<OperationResult> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      async: String(invokeAsync),
    });
    const [request, response] = await this.request(
      'post',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/invoke`,
      params,
      JSON.stringify(operationRequest),
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async invokeOperationValue(
    submodelId: string,
    idShortPath: string,
    operationRequest: OperationRequestValue,
    invokeAsync?: boolean,
    signal?: AbortSignal,
  ): Promise<OperationResultValue> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      async: String(invokeAsync),
    });
    const [request, response] = await this.request(
      'post',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/invoke/$value`,
      params,
      JSON.stringify(operationRequest),
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async invokeOperationAsync(
    submodelId: string,
    idShortPath: string,
    operationRequest: OperationRequest,
    signal?: AbortSignal,
  ): Promise<string> {
    const [request, response] = await this.request(
      'post',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/invoke-async`,
      undefined,
      JSON.stringify(operationRequest),
      signal,
    );
    await assertValidAasResponse(request, response, 'void');
    return response.headers.get('Location') ?? '';
  }

  public async invokeOperationAsyncValue(
    submodelId: string,
    idShortPath: string,
    operationRequest: OperationRequestValue,
    signal?: AbortSignal,
  ): Promise<string> {
    const [request, response] = await this.request(
      'post',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/invoke-async/$value`,
      undefined,
      JSON.stringify(operationRequest),
      signal,
    );
    await assertValidAasResponse(request, response, 'void');
    return response.headers.get('Location') ?? '';
  }

  public async getOperationStatus(
    submodelId: string,
    idShortPath: string,
    handleId: string,
    signal?: AbortSignal,
  ): Promise<BaseOperationResult> {
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/operation-status/${base64Url(handleId)}`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getOperationResults(
    submodelId: string,
    idShortPath: string,
    handleId: string,
    signal?: AbortSignal,
  ): Promise<OperationResult> {
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/operation-results/${base64Url(handleId)}`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getOperationResultsValue(
    submodelId: string,
    idShortPath: string,
    handleId: string,
    signal?: AbortSignal,
  ): Promise<OperationResultValue> {
    const [request, response] = await this.request(
      'get',
      `/submodels/${base64Url(submodelId)}/submodel-elements/${idShortPath}/operation-results/${base64Url(handleId)}/$value`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }
}
